/**
 * The « l'un de » GROUPS of an objective's criteria (issue #181,
 * scenario-succes-conduite.md §4.1, refonte modulaire §7.2).
 *
 * The list of citations stays FLAT; a citation may carry an `orGroup`, and the
 * neighbouring citations of the same value form one group, « l'un de »: filled
 * as soon as one of them is. A citation with no `orGroup` is a group of its own,
 * so a protocol written before the field existed is cut into one group per
 * criterion and reads exactly as before.
 *
 * This is the ONE place the groups are cut. The engine reads them through it
 * (`objectiveProgress`), the editor draws the framed group off it, and the
 * server's views list the criteria by it: three walks would be three chances to
 * disagree on where a group ends.
 */

/** What the cut needs of a citation: its optional group label. */
export type OrGroupCarrier = { readonly orGroup?: string | undefined };

/**
 * One group: the citations of a same `orGroup` run, or a lone citation.
 *
 * `orGroup` is the shared label, or null for a lone citation without one.
 * `either` is true for a real « l'un de » group - a labelled run - and false
 * for a lone one; a labelled run of a single citation is `either` all the same
 * (it is what `orGroupProblems` refuses), so a reader never mistakes it for a
 * plain criterion.
 */
export type CriterionGroup<T extends OrGroupCarrier> = {
  readonly orGroup: string | null;
  readonly either: boolean;
  readonly criteria: readonly T[];
};

/**
 * Cut the citations of an objective into groups, in their order: each run of
 * neighbouring citations sharing an `orGroup` is one group, every citation
 * without one is a group alone.
 *
 * Accepts the objective, or its bare list of citations.
 */
export function objectiveCriterionGroups<T extends OrGroupCarrier>(
  objective: { readonly criteria: readonly T[] } | readonly T[],
): CriterionGroup<T>[] {
  const criteria: readonly T[] = 'criteria' in objective ? objective.criteria : objective;
  const groups: { orGroup: string | null; either: boolean; criteria: T[] }[] = [];
  for (const criterion of criteria) {
    const label = criterion.orGroup ?? null;
    const last = groups[groups.length - 1];
    if (label !== null && last !== undefined && last.orGroup === label) {
      last.criteria.push(criterion);
      continue;
    }
    groups.push({ orGroup: label, either: label !== null, criteria: [criterion] });
  }
  return groups;
}

/**
 * The « l'un de » labels of an objective that are NOT a valid group (issue #181,
 * H2): a group of a single citation, and a label used again after another
 * citation came between - the later run is no group of its own, and reading it
 * as a second group would make one label name two.
 *
 * Each label is named once per defect, in the order the objective lists them.
 */
export function orGroupProblems(
  objective: { readonly criteria: readonly OrGroupCarrier[] } | readonly OrGroupCarrier[],
): string[] {
  const refused: string[] = [];
  const closed = new Set<string>();
  for (const group of objectiveCriterionGroups(objective)) {
    if (group.orGroup === null) continue;
    const reused = closed.has(group.orGroup);
    closed.add(group.orGroup);
    if ((reused || group.criteria.length < 2) && !refused.includes(group.orGroup)) {
      refused.push(group.orGroup);
    }
  }
  return refused;
}
